import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

type HoldStatus = 'ACTIVE' | 'CHARGED' | 'CANCELLED' | 'EXPIRED' | 'RELEASED';
type Source = 'BOOKING_COM' | 'EXPEDIA_TAAP' | 'AGODA' | 'UNKNOWN';

interface HoldRow {
  id: string;
  source: Source;
  externalRef: string;
  guestName: string | null;
  propertyName: string | null;
  checkIn: string | null;
  checkOut: string | null;
  expectedChargeDate: string | null;
  expectedAmount: string | null;
  currency: string | null;
  cardLastFour: string | null;
  cardNickname: string | null;
  clientId: string | null;
  clientName: string | null;
  tripId: string | null;
  tripName: string | null;
  freeCancelUntil: string | null;
  status: HoldStatus;
  chargedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
}

const SOURCE_META: Record<Source, { label: string; tone: string }> = {
  BOOKING_COM: { label: 'Booking.com', tone: 'bg-blue-500/15 text-blue-600 dark:text-blue-400' },
  EXPEDIA_TAAP: { label: 'Expedia TAAP', tone: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  AGODA: { label: 'Agoda', tone: 'bg-rose-500/15 text-rose-600 dark:text-rose-400' },
  UNKNOWN: { label: 'Unknown', tone: 'bg-muted text-muted-foreground' },
};

const STATUS_META: Record<HoldStatus, { label: string; tone: string }> = {
  ACTIVE: { label: 'Active', tone: 'bg-warning/15 text-warning' },
  CHARGED: { label: 'Charged', tone: 'bg-success/15 text-success' },
  CANCELLED: { label: 'Cancelled', tone: 'bg-muted text-muted-foreground' },
  EXPIRED: { label: 'Expired', tone: 'bg-destructive/15 text-destructive' },
  RELEASED: { label: 'Released', tone: 'bg-muted text-muted-foreground' },
};

function formatMoney(amount: string | null, currency: string | null): string {
  if (!amount) return '—';
  const n = parseFloat(amount);
  if (!isFinite(n)) return amount;
  const c = currency ?? 'INR';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

type GroupBy = 'portal' | 'date' | 'flat';

export function HoldsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<HoldStatus | 'ALL'>('ACTIVE');
  const [groupBy, setGroupBy] = useState<GroupBy>('portal');

  const list = useQuery<HoldRow[]>({
    queryKey: ['holds', filter],
    queryFn: () => api(filter === 'ALL' ? '/api/holds' : `/api/holds?status=${filter}`),
    refetchInterval: 60_000,
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api(`/api/holds/${id}/request-cancel`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['holds'] });
      qc.invalidateQueries({ queryKey: ['cancel-queue'] });
    },
  });

  const isAdmin = user?.role === 'ADMIN';
  const rows = list.data ?? [];

  // Per-portal totals for the summary strip across the top.
  const portalSummary = useMemo(() => {
    const out: Record<Source, { count: number; totalsByCcy: Record<string, number> }> = {
      BOOKING_COM: { count: 0, totalsByCcy: {} },
      EXPEDIA_TAAP: { count: 0, totalsByCcy: {} },
      AGODA: { count: 0, totalsByCcy: {} },
      UNKNOWN: { count: 0, totalsByCcy: {} },
    };
    for (const h of rows) {
      out[h.source].count++;
      if (h.expectedAmount && h.currency) {
        const n = parseFloat(h.expectedAmount);
        if (isFinite(n)) {
          out[h.source].totalsByCcy[h.currency] = (out[h.source].totalsByCcy[h.currency] ?? 0) + n;
        }
      }
    }
    return out;
  }, [rows]);

  // Build groups based on selection
  const groups = useMemo(() => buildGroups(rows, groupBy), [rows, groupBy]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Holds</h1>
        <p className="text-sm text-muted-foreground">
          Pre-paid bookings on Booking.com / Expedia / Agoda. Cards charge automatically near
          check-in — released bookings free up the card before the cancellation deadline.
        </p>
      </div>

      {/* Portal summary cards — always visible so you can see the spread at a glance */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(['BOOKING_COM', 'EXPEDIA_TAAP', 'AGODA'] as const).map((src) => {
          const meta = SOURCE_META[src];
          const s = portalSummary[src];
          return (
            <Card key={src}>
              <CardContent className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide', meta.tone)}>
                    {meta.label}
                  </span>
                  <span className="text-2xl font-semibold tabular-nums">{s.count}</span>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  {Object.keys(s.totalsByCcy).length === 0
                    ? '—'
                    : Object.entries(s.totalsByCcy)
                        .map(([ccy, total]) => formatMoney(total.toFixed(2), ccy))
                        .join(' · ')}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1.5">
          {(['ACTIVE', 'CHARGED', 'RELEASED', 'CANCELLED', 'EXPIRED', 'ALL'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                filter === s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground',
              )}
            >
              {s === 'ALL' ? 'All' : STATUS_META[s as HoldStatus].label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span>Group by</span>
          {(['portal', 'date', 'flat'] as const).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGroupBy(g)}
              className={cn(
                'rounded px-2 py-1 font-medium transition-colors',
                groupBy === g ? 'bg-secondary text-foreground' : 'hover:text-foreground',
              )}
            >
              {g === 'portal' ? 'Portal' : g === 'date' ? 'Charge date' : 'Flat'}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">
              <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              No holds in this view.
            </p>
          ) : (
            <div>
              {groups.map((g) => (
                <section key={g.key}>
                  <header className="flex items-center justify-between border-b bg-muted/30 px-5 py-2">
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {g.label}
                    </h2>
                    <span className="text-[11px] tabular-nums text-muted-foreground">
                      {g.holds.length} {g.holds.length === 1 ? 'hold' : 'holds'}
                      {g.subtotalLabel ? ` · ${g.subtotalLabel}` : ''}
                    </span>
                  </header>
                  <ul className="divide-y">
                    {g.holds.map((h) => (
                      <HoldRowItem
                        key={h.id}
                        hold={h}
                        isAdmin={isAdmin}
                        onCancel={() => cancel.mutate(h.id)}
                        cancelling={cancel.isPending}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface HoldGroup {
  key: string;
  label: string;
  subtotalLabel?: string;
  holds: HoldRow[];
}

function buildGroups(rows: HoldRow[], groupBy: GroupBy): HoldGroup[] {
  if (rows.length === 0) return [];

  if (groupBy === 'flat') {
    const sorted = [...rows].sort(sortByChargeAsc);
    return [{ key: 'all', label: 'All holds', holds: sorted }];
  }

  if (groupBy === 'portal') {
    // Order: Booking → Expedia → Agoda → Unknown
    const order: Source[] = ['BOOKING_COM', 'EXPEDIA_TAAP', 'AGODA', 'UNKNOWN'];
    const buckets: Record<Source, HoldRow[]> = {
      BOOKING_COM: [], EXPEDIA_TAAP: [], AGODA: [], UNKNOWN: [],
    };
    for (const r of rows) buckets[r.source].push(r);
    const groups: HoldGroup[] = [];
    for (const src of order) {
      if (buckets[src].length === 0) continue;
      const sorted = buckets[src].sort(sortByChargeAsc);
      groups.push({
        key: src,
        label: SOURCE_META[src].label,
        subtotalLabel: subtotalText(sorted),
        holds: sorted,
      });
    }
    return groups;
  }

  // groupBy === 'date' — bucket by week of expected_charge_date
  const buckets = new Map<string, { label: string; holds: HoldRow[]; sortKey: number }>();
  const now = new Date();
  for (const r of rows) {
    const { key, label, sortKey } = bucketByDate(r.expectedChargeDate, now);
    const b = buckets.get(key) ?? { label, holds: [], sortKey };
    b.holds.push(r);
    buckets.set(key, b);
  }
  return Array.from(buckets.entries())
    .sort((a, b) => a[1].sortKey - b[1].sortKey)
    .map(([key, b]) => ({
      key,
      label: b.label,
      subtotalLabel: subtotalText(b.holds.sort(sortByChargeAsc)),
      holds: b.holds,
    }));
}

function sortByChargeAsc(a: HoldRow, b: HoldRow): number {
  const ad = a.expectedChargeDate ? new Date(a.expectedChargeDate).getTime() : Infinity;
  const bd = b.expectedChargeDate ? new Date(b.expectedChargeDate).getTime() : Infinity;
  return ad - bd;
}

function bucketByDate(iso: string | null, now: Date): { key: string; label: string; sortKey: number } {
  if (!iso) return { key: 'no-date', label: 'No charge date set', sortKey: 1e15 };
  const d = new Date(iso);
  const days = Math.floor((d.getTime() - now.getTime()) / 86_400_000);
  if (days < 0) return { key: 'overdue', label: 'Charge date passed (no debit yet)', sortKey: -1 };
  if (days <= 7) return { key: 'thisweek', label: 'Next 7 days', sortKey: 0 };
  if (days <= 30) return { key: 'thismonth', label: 'Next 8–30 days', sortKey: 1 };
  if (days <= 90) return { key: 'thisquarter', label: '1–3 months', sortKey: 2 };
  return { key: 'later', label: 'Beyond 3 months', sortKey: 3 };
}

function subtotalText(holds: HoldRow[]): string | undefined {
  const totals: Record<string, number> = {};
  for (const h of holds) {
    if (!h.expectedAmount || !h.currency) continue;
    const n = parseFloat(h.expectedAmount);
    if (!isFinite(n)) continue;
    totals[h.currency] = (totals[h.currency] ?? 0) + n;
  }
  if (Object.keys(totals).length === 0) return undefined;
  return Object.entries(totals)
    .map(([ccy, total]) => formatMoney(total.toFixed(2), ccy))
    .join(' · ');
}

function HoldRowItem({
  hold,
  isAdmin,
  onCancel,
  cancelling,
}: {
  hold: HoldRow;
  isAdmin: boolean;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const sourceMeta = SOURCE_META[hold.source];
  const statusMeta = STATUS_META[hold.status];
  const cancelDays = daysUntil(hold.freeCancelUntil);
  const chargeDays = daysUntil(hold.expectedChargeDate);

  // Urgency colour for the free-cancel countdown.
  const cancelTone =
    cancelDays === null
      ? 'text-muted-foreground'
      : cancelDays < 0
        ? 'text-destructive'
        : cancelDays <= 2
          ? 'text-warning'
          : 'text-muted-foreground';

  return (
    <li className="flex flex-wrap items-start gap-4 px-5 py-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide', sourceMeta.tone)}>
            {sourceMeta.label}
          </span>
          <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide', statusMeta.tone)}>
            {statusMeta.label}
          </span>
          <span className="font-mono text-xs text-muted-foreground">#{hold.externalRef}</span>
        </div>

        <div className="mt-1 truncate text-sm font-medium">
          {hold.guestName ?? '—'}
          {hold.propertyName ? <span className="ml-2 text-muted-foreground">· {hold.propertyName}</span> : null}
        </div>

        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          {hold.checkIn ? (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDate(hold.checkIn)} → {formatDate(hold.checkOut)}
            </span>
          ) : null}
          {hold.cardLastFour ? <span>card ••{hold.cardLastFour}</span> : <span>card unknown</span>}
          {hold.clientName ? <span>client: {hold.clientName}</span> : null}
        </div>
      </div>

      <div className="text-right">
        <div className="text-sm font-semibold tabular-nums">
          {formatMoney(hold.expectedAmount, hold.currency)}
        </div>
        {hold.expectedChargeDate ? (
          <div className="text-[11px] text-muted-foreground">
            charge {chargeDays !== null && chargeDays >= 0 ? `in ${chargeDays}d` : `${Math.abs(chargeDays!)}d ago`}
          </div>
        ) : null}
        {hold.freeCancelUntil ? (
          <div className={cn('mt-0.5 text-[11px]', cancelTone)}>
            free cancel{' '}
            {cancelDays !== null && cancelDays >= 0
              ? `for ${cancelDays}d`
              : 'window closed'}
          </div>
        ) : null}
        {isAdmin && hold.status === 'ACTIVE' ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={onCancel}
            disabled={cancelling}
          >
            {cancelling ? 'Sending…' : 'Cancel booking'}
          </Button>
        ) : null}
      </div>
    </li>
  );
}
